/**
 * useRegenerateOffPageBrief — `POST /v1/off-page-briefs/:opbId/regenerate`.
 *
 * Kicks off a fresh workflow run (plan → critique → ground → persist) for
 * either all previously requested platforms (body omitted) or a subset
 * (`platforms: ['reddit']` for a "Regen this variant" action).
 *
 * The endpoint may return `OFF_PAGE_BRIEF_ALREADY_REGENERATING` when a
 * concurrent regenerate is already in-flight; callers should pattern-match
 * on `error.code` and surface a distinct "Already regenerating…" message
 * (not a generic error) so the tenant knows to wait rather than retry.
 *
 * Invalidates both list + detail caches so the workspace's status card
 * transitions to `planning` immediately on success.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateOffPageBriefResponse,
  RegenerateOffPageBriefRequest,
} from '@seo/types';
import { apiPost } from '@/lib/api';

export function useRegenerateOffPageBrief(opbId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RegenerateOffPageBriefRequest = {}) =>
      apiPost<CreateOffPageBriefResponse>(
        `/v1/off-page-briefs/${opbId}/regenerate`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'detail', opbId],
      });
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'list'],
      });
    },
  });
}
